// PermissionProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserRights } from '../../context/UserRightsContext';
import { CircularProgress, Box } from '@mui/material';
import ProtectedRoute from './ProtectedRoute';
import { NO_ACCESS_PATH, resolveDefaultAppPath } from '../../utils/defaultAppPath';
import { isHarmonateUser as isServerHarmonateUser } from '../../auth/serverIdentity';

/**
 * Protects routes based on user permissions for a function code.
 */
const PermissionProtectedRoute = ({
  children,
  functionCode,
  functionCodes,
  requiredPermission = 'view',
  fallbackPath = '/dashboard',
}) => {
  const { hasPermission, canAccessMenu, loading, rightsLoaded } = useUserRights();
  const location = useLocation();
  const isHarmonateUser = isServerHarmonateUser();

  if (isHarmonateUser) {
    return children;
  }

  if (loading || !rightsLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const codes = [
    ...(Array.isArray(functionCodes) ? functionCodes : []),
    ...(functionCode ? [functionCode] : []),
  ].filter(Boolean);

  if (!codes.length) {
    return children;
  }

  const allowed = codes.some(
    (code) => canAccessMenu(code) || hasPermission(code, requiredPermission)
  );

  if (!allowed) {
    const resolved = resolveDefaultAppPath(canAccessMenu, false);
    const next = fallbackPath && fallbackPath !== '/dashboard' ? fallbackPath : resolved;
    const target = !next || next === location.pathname ? NO_ACCESS_PATH : next;
    if (location.pathname === target) {
      return <Navigate to={NO_ACCESS_PATH} replace />;
    }
    return <Navigate to={target} replace />;
  }

  return children;
};

/**
 * Combined auth + permission route wrapper.
 */
export const AuthAndPermissionRoute = ({
  children,
  functionCode,
  functionCodes,
  requiredPermission = 'view',
  fallbackPath = '/dashboard',
}) => {
  return (
    <ProtectedRoute>
      <PermissionProtectedRoute
        functionCode={functionCode}
        functionCodes={functionCodes}
        requiredPermission={requiredPermission}
        fallbackPath={fallbackPath}
      >
        {children}
      </PermissionProtectedRoute>
    </ProtectedRoute>
  );
};

export default PermissionProtectedRoute;
